import {
  COMPLIANCE_DOCUMENT_STATUS_LABELS,
  COMPLIANCE_SCOPE_LABELS,
  type ComplianceDocumentDetail,
} from "@station/contracts";
import { Alert, Button, Card, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SearchX } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { EXPIRY_TONES, expiryText } from "../../../../features/compliance/compliance-values.js";
import {
  ArchiveDocumentDialog,
  type DocumentMode,
  DocumentFormDialog,
} from "../../../../features/compliance/document-dialogs.js";
import { useComplianceDocument } from "../../../../features/compliance/queries.js";
import { useMaintenancePermissions } from "../../../../features/maintenance/queries.js";
import { DetailRow, FileOpenButton, MaintenancePage } from "../../../../features/maintenance/shared.js";
import { errorMessage, isApiError } from "../../../../lib/errors.js";
import { formatDate, formatDateTime } from "../../../../lib/format.js";

export const Route = createFileRoute("/_app/uyum/belgeler/$id")({ component: DocumentRoute });

/**
 * Uyum belgesi (Prompt 23 web, `GET /compliance/documents/:id`): tür, kapsam, belge no, veriliş / bitiş ve hatırlatma, dosya,
 * sorumlu, not; yenileme geçmişi. Eylemler (yetki `canManage`): yenile, düzelt, arşivle.
 */
function DocumentRoute(): ReactElement {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const permissions = useMaintenancePermissions();
  const document = useComplianceDocument(id, permissions.enabled && permissions.canReadCompliance);
  const [dialog, setDialog] = useState<DocumentMode | "archive" | null>(null);
  const data = document.data;
  const title = data?.type.name ?? t("compliance.documents.detailTitle");
  const editable = data?.canManage && data.status === "ACTIVE";
  return (
    <MaintenancePage
      title={title}
      {...(data
        ? {
            description: `${data.stationCode ? `${data.stationCode} ${data.stationName}` : data.companyName}${data.asset ? ` · ${data.asset.name}` : ""}`,
          }
        : {})}
      requirement="compliance"
      breadcrumb={[
        {
          label: t("menu.complianceDocuments"),
          link: (children, className) => (
            <Link to="/uyum/belgeler" className={className}>
              {children}
            </Link>
          ),
        },
        { label: data?.documentNo ?? title },
      ]}
      actions={
        editable ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setDialog("renew")}>{t("compliance.documents.renew")}</Button>
            <Button variant="outline" onClick={() => setDialog("edit")}>
              {t("compliance.documents.edit")}
            </Button>
            <Button variant="ghost" onClick={() => setDialog("archive")}>
              {t("compliance.documents.archive")}
            </Button>
          </div>
        ) : null
      }
    >
      {document.error ? (
        isApiError(document.error, "NOT_FOUND") ? (
          <EmptyState
            headingLevel={2}
            icon={<SearchX />}
            title={t("compliance.documents.notFoundTitle")}
            description={t("compliance.documents.notFoundDescription")}
          />
        ) : (
          <Alert tone="danger">{errorMessage(document.error)}</Alert>
        )
      ) : !data ? (
        <SkeletonRows rows={6} />
      ) : (
        <DocumentBody document={data} />
      )}
      {data && (dialog === "renew" || dialog === "edit") ? (
        <DocumentFormDialog open onOpenChange={() => setDialog(null)} mode={dialog} document={data} />
      ) : null}
      {data && dialog === "archive" ? (
        <ArchiveDocumentDialog open onOpenChange={() => setDialog(null)} document={data} />
      ) : null}
    </MaintenancePage>
  );
}

function DocumentBody({ document }: { document: ComplianceDocumentDetail }): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      <Card className="grid gap-4 p-4">
        <div className="flex flex-wrap items-center gap-2">
          {document.status === "ACTIVE" ? (
            <StatusBadge tone={EXPIRY_TONES[document.expiry.status]}>
              {expiryText(document.expiry)}
            </StatusBadge>
          ) : (
            <StatusBadge>{COMPLIANCE_DOCUMENT_STATUS_LABELS[document.status]}</StatusBadge>
          )}
          {document.type.verified ? null : (
            <StatusBadge tone="warning">{t("compliance.types.unverifiedBadge")}</StatusBadge>
          )}
        </div>
        <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DetailRow label={t("compliance.fields.appliesTo")}>
            {COMPLIANCE_SCOPE_LABELS[document.type.appliesTo]}
          </DetailRow>
          <DetailRow label={t("compliance.fields.documentNo")}>{document.documentNo ?? "—"}</DetailRow>
          <DetailRow label={t("compliance.fields.issuedOn")}>{formatDate(document.issuedOn)}</DetailRow>
          <DetailRow label={t("compliance.fields.expiresOn")}>
            {document.expiresOn ? formatDate(document.expiresOn) : t("compliance.documents.noExpiry")}
          </DetailRow>
          <DetailRow label={t("compliance.fields.nextReminderOn")}>
            {formatDate(document.expiry.nextReminderOn)}
          </DetailRow>
          <DetailRow label={t("compliance.fields.responsible")}>
            {document.responsibleUser?.name ?? "—"}
          </DetailRow>
          <DetailRow label={t("compliance.fields.company")}>{document.companyName}</DetailRow>
          <DetailRow label={t("compliance.fields.createdAt")}>{formatDateTime(document.createdAt)}</DetailRow>
        </dl>
        {document.notes ? <p className="whitespace-pre-line text-sm">{document.notes}</p> : null}
        {document.archiveReason ? (
          <Alert tone="info" title={t("compliance.fields.reason")}>
            {document.archiveReason}
          </Alert>
        ) : null}
        {document.fileId ? (
          <FileOpenButton fileId={document.fileId} label={t("compliance.documents.openFile")} />
        ) : null}
      </Card>
      <Card className="grid gap-3 p-4">
        <h2 className="text-base font-semibold">{t("compliance.documents.history")}</h2>
        {document.history.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("compliance.documents.noHistory")}</p>
        ) : (
          <ul className="grid gap-2">
            {document.history.map((item) => (
              <li
                key={item.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm"
              >
                <Link
                  to="/uyum/belgeler/$id"
                  params={{ id: item.id }}
                  className="underline underline-offset-4"
                >
                  {item.documentNo ?? t("compliance.documents.noNumber")}
                </Link>
                <span className="text-muted-foreground">
                  {formatDate(item.issuedOn)} – {formatDate(item.expiresOn)} ·{" "}
                  {COMPLIANCE_DOCUMENT_STATUS_LABELS[item.status]}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
